import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { ThemeProvider } from '@mui/material';
import NoDataMessage from '../../components/NoDataMessage/NoDataMessage.jsx';

const defaultColors = ['#53b025', '#2729a1', '#c71818'];

// ----------------------------------------------------------------------
// Custom Tooltip (filters out dummy + forces all text to white)
// ----------------------------------------------------------------------
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  // Remove dummy
  const filtered = payload.filter(item => item.dataKey !== 'dummy');
  if (filtered.length === 0) return null;

  return (
    <div
      style={{
        background: "rgba(0,0,0,0.8)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: "8px",
        padding: "8px 12px",
      }}
    >
      <p style={{ color: "#ffffff", fontWeight: "bold", marginBottom: 6 }}>
        {label}
      </p>

      {filtered.map((item, index) => (
        <p key={index} style={{ color: "#ffffff", margin: 0 }}>
          {item.name}: {item.value}
        </p>
      ))}
    </div>
  );
};

const LineChart = ({
  dataSources,
  selectedFeature = 'All',
  chartTheme,
  height = 320,
  width = '100%',
  noDataMessage = 'No Data Available',
}) => {

  // Build merged chart data and prepare label field for LabelList (only showing up to 7 labels)
  const chartData = useMemo(() => {
    if (!dataSources || dataSources.length === 0 || !dataSources[0].data.length) return [];

    const baseData = dataSources[0].data;

    // preliminary mapped data with formatted dates
    const mapped = baseData.map((item, index) => {
      const dateObj = new Date(item.date);
      const formatted = dateObj.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
      });

      const point = {
        date: formatted,
        _rawTimestamp: dateObj.getTime(),
        dummy: 0,
      };

      dataSources.forEach(source => {
        point[source.key] = source.data[index]?.count || 0;
      });

      return point;
    });

    // Determine which indices will show labels (max 7)
    const n = mapped.length;
    if (n <= 8) {
      // show all labels
      return mapped.map(p => ({ ...p, label: p.date }));
    }

    const maxLabels = 7;
    const labelIndices = new Set();
    for (let i = 0; i < maxLabels; i++) {
      const idx = Math.round((i * (n - 1)) / (maxLabels - 1));
      labelIndices.add(idx);
    }

    // produce final array with label only on chosen indices, others get empty string
    return mapped.map((p, i) => ({
      ...p,
      label: labelIndices.has(i) ? p.date : '',
    }));
  }, [dataSources]);

  const hasNoData = useMemo(() => {
    return (
      chartData.length === 0 ||
      dataSources.every(src => src.data.every(d => d.count === 0))
    );
  }, [chartData, dataSources]);

  const visibleLines = useMemo(() => {
    return dataSources
      .filter(source => {
        if (selectedFeature === 'All') return true;
        return (
          source.key === selectedFeature ||
          (selectedFeature === 'Interview Scheduled' &&
            source.key === 'Interviews Scheduled')
        );
      })
      .map((source, i) => ({
        key: source.key,
        color: source.color || defaultColors[i % defaultColors.length],
      }));
  }, [dataSources, selectedFeature]);


  if (hasNoData) {
    return (
      <div className="h-[260px] flex items-center justify-center">
        <NoDataMessage message={noDataMessage} />
      </div>
    );
  }

  return (
    <ThemeProvider theme={chartTheme}>
      <div className="w-full max-w-[551px] mx-auto mt-0 mb-0">
        <div className="rounded-xl p-1 py-2" style={{ height, width }}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsLineChart
              data={chartData}
              margin={{ top: 15, right: 20, left: 10, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              {/* hide default ticks because we render labels via the dummy Line's LabelList */}
              <XAxis dataKey="date" tick={false} height={60} />
              <YAxis tick={{ fill: '#d3d3d3', fontSize: 11 }} width={40} />

              {/* Tooltip with white text */}
              <Tooltip
                content={<CustomTooltip />}
                cursor={{
                  stroke: "#B794FF",
                  strokeWidth: 2,
                  strokeDasharray: "6 6",
                }}
              />

              {visibleLines.map(line => (
                <Line
                  key={line.key}
                  type="monotone"
                  dataKey={line.key}
                  stroke={line.color}
                  strokeWidth={3}
                  dot={{ fill: line.color, r: 5 }}
                  activeDot={{ r: 7 }}
                  connectNulls
                />
              ))}

              {/* Dummy line used purely for rendering labels at the bottom.
                  Only points chosen in chartData have a non-empty `label` value,
                  so LabelList will render at most 7 labels evenly spaced. */}
              <Line
                type="monotone"
                dataKey="dummy"
                stroke="transparent"
                dot={false}
                activeDot={false}
                connectNulls
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="label"
                  position="bottom"
                  offset={15}
                  style={{
                    fill: '#D1D5DB',
                    fontSize: 11.5,
                    fontWeight: 500,
                    textAnchor: 'middle',
                    opacity: 0.95,
                  }}
                />
              </Line>
            </RechartsLineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </ThemeProvider>
  );
};

LineChart.propTypes = {
  dataSources: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string.isRequired,
          count: PropTypes.number.isRequired,
        })
      ).isRequired,
      color: PropTypes.string,
    })
  ).isRequired,
  selectedFeature: PropTypes.string,
  chartTheme: PropTypes.object.isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  noDataMessage: PropTypes.string,
};

export default LineChart;
